"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import { signOut, useSession } from "next-auth/react";
import { Link, usePathname } from "@/i18n/navigation";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { NotificationBell } from "@/components/NotificationBell";
import { ThemeToggle } from "./ThemeToggle";
import { UserMenu } from "./UserMenu";
import { buttonClass } from "./ui/Button";

export type NavUser = {
  name: string | null;
  email: string | null;
  role: "USER" | "ADMIN";
} | null;

const NAV_LINKS = [
  { href: "/", key: "home" },
  { href: "/stays", key: "stays" },
  { href: "/partners", key: "partnerStays" },
  { href: "/book", key: "book" },
  { href: "/contact", key: "contact" },
] as const;

export function NavBar() {
  const t = useTranslations("Nav");
  const pathname = usePathname();
  const [open, setOpen] = useState(false);

  // Session read on the client so the layout stays static (bfcache-eligible).
  const { data: session, status } = useSession();
  const ready = status !== "loading";
  const user: NavUser = session?.user
    ? {
        name: session.user.name ?? null,
        email: session.user.email ?? null,
        role: (session.user.role as "USER" | "ADMIN") ?? "USER",
      }
    : null;

  const linkClass = (href: string) => {
    const active = href === "/" ? pathname === "/" : pathname.startsWith(href);
    return `text-sm font-medium transition-colors ${
      active ? "text-forest-600" : "text-bark/80 hover:text-forest-600"
    }`;
  };

  // Remember the current page so login/register can return to it.
  const from =
    pathname &&
    !pathname.startsWith("/login") &&
    !pathname.startsWith("/register")
      ? pathname
      : null;
  const loginHref = from
    ? { pathname: "/login", query: { callbackUrl: from } }
    : "/login";
  const registerHref = from
    ? { pathname: "/register", query: { callbackUrl: from } }
    : "/register";

  return (
    <header className="sticky top-0 z-50 border-b border-sand/80 bg-cream/85 backdrop-blur">
      <nav className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
        {/* Logo */}
        <Link href="/" className="flex items-center gap-2">
          <span className="grid h-9 w-9 place-items-center rounded-full bg-forest-500 text-white">
            <LeafIcon />
          </span>
          <span className="font-display text-lg font-bold text-bark">
            Welcome House
          </span>
        </Link>

        {/* Desktop links */}
        <div className="hidden items-center gap-7 md:flex">
          {NAV_LINKS.map((l) => (
            <Link
              key={l.key}
              href={l.href}
              className={linkClass(l.href)}
            >
              {t(l.key)}
            </Link>
          ))}
        </div>

        {/* Right side */}
        <div className="hidden items-center gap-3 md:flex">
          <ThemeToggle />
          <LanguageSwitcher />
          {ready &&
            (user ? (
              <>
                <NotificationBell />
                <UserMenu user={user} />
              </>
            ) : (
              <div className="flex items-center gap-2">
                <Link href={loginHref} className={buttonClass("ghost", "md")}>
                  {t("login")}
                </Link>
                <Link
                  href={registerHref}
                  className={buttonClass("primary", "md")}
                >
                  {t("register")}
                </Link>
              </div>
            ))}
        </div>

        {/* Mobile toggle */}
        <button
          className="grid h-10 w-10 place-items-center rounded-full text-bark md:hidden"
          onClick={() => setOpen((o) => !o)}
          aria-label="Menu"
          aria-expanded={open}
        >
          {open ? <CloseIcon /> : <MenuIcon />}
        </button>
      </nav>

      {/* Mobile menu */}
      {open && (
        <div className="border-t border-sand/80 bg-cream md:hidden">
          <div className="mx-auto flex max-w-6xl flex-col gap-3 px-4 py-4">
            {NAV_LINKS.map((l) => (
              <Link
                key={l.key}
                href={l.href}
                className={linkClass(l.href)}
                onClick={() => setOpen(false)}
              >
                {t(l.key)}
              </Link>
            ))}
            <div className="flex items-center gap-2 pt-2">
              <ThemeToggle align="left" />
              <LanguageSwitcher />
            </div>
            <div className="flex flex-col gap-2 pt-2">
              {ready &&
                (user ? (
                <>
                  <div className="mb-1 border-t border-sand/80 pt-3">
                    <p className="truncate text-sm font-semibold text-bark">
                      {user.name ?? t("account")}
                    </p>
                    {user.email && (
                      <p className="truncate text-xs text-stone">
                        {user.email}
                      </p>
                    )}
                  </div>
                  <NotificationBell variant="row" />
                  {user.role === "ADMIN" && (
                    <Link
                      href="/admin"
                      className={buttonClass("secondary", "md")}
                      onClick={() => setOpen(false)}
                    >
                      {t("admin")}
                    </Link>
                  )}
                  <Link
                    href="/account"
                    className={buttonClass("secondary", "md")}
                    onClick={() => setOpen(false)}
                  >
                    {t("account")}
                  </Link>
                  <button
                    onClick={() => signOut({ callbackUrl: "/" })}
                    className={buttonClass("ghost", "md")}
                  >
                    {t("logout")}
                  </button>
                </>
              ) : (
                <>
                  <Link
                    href={loginHref}
                    className={buttonClass("secondary", "md")}
                    onClick={() => setOpen(false)}
                  >
                    {t("login")}
                  </Link>
                  <Link
                    href={registerHref}
                    className={buttonClass("primary", "md")}
                    onClick={() => setOpen(false)}
                  >
                    {t("register")}
                  </Link>
                </>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

function LeafIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M5 21c0-9 7-16 16-16 0 9-7 16-16 16zM5 21c4-4 7-6 11-8"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function MenuIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M4 7h16M4 12h16M4 17h16"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M6 6l12 12M18 6L6 18"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}
